import "../shared/IntakeForm.scss";

import Button from "components/Button/Button";
import { ControlledInput } from "components/Card/ControlledInput";
import KaboodleToaster from "components/common/KaboodleToaster";
import { TextInput } from "components/inputs";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import type { ConfirmPasswordResetRequest } from "services/intake-api";
import { useConfirmPasswordResetMutation } from "services/intake-api";

import IntakeHeader from "../components/HeaderIntake";
import { useOrganizationSlug } from "../hooks/useOrganizationSlug";
import { logToSentry, getSessionId } from "utils/sentry";

type ResetPasswordFormData = {
  password1: string;
  password2: string;
};

const ResetPasswordIntake = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const organizationSlug = useOrganizationSlug();

  const {
    control,
    handleSubmit: handleFormSubmit,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<ResetPasswordFormData>({
    defaultValues: {
      password1: "",
      password2: "",
    },
  });

  const [confirmPasswordReset, { isLoading }] =
    useConfirmPasswordResetMutation();
  const [isCompleted, setIsCompleted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const password1 = watch("password1");

  useEffect(() => {
    if (!token) {
      navigate(`/intake/${organizationSlug || "allan-koba"}/auth/login`);
    }
  }, [token, navigate, organizationSlug]);

  const handleFieldChange = async () => Promise.resolve();
  const uiSessionId = getSessionId();

  useEffect(() => {
    logToSentry(`Intake reset password page`, "info", { uiSessionId });
  }, [uiSessionId]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    const uiSessionId = getSessionId();

    if (!token) {
      setError("Invalid reset token. Please request a new password reset.");
      KaboodleToaster.error(
        "Invalid reset token. Please request a new password reset.",
      );
      return;
    }

    try {
      const requestData: ConfirmPasswordResetRequest = {
        password_reset_token: token,
        password1: data.password1,
        password2: data.password2,
      };

      await confirmPasswordReset(requestData).unwrap();
      setIsCompleted(true);
      setError(null);
      KaboodleToaster.success(
        "Password reset successful! You can now log in with your new password.",
      );
      logToSentry(`Intake password reset successful`, "info", { uiSessionId });
    } catch (err: unknown) {
      let errorMessage = "Failed to reset password. Please try again.";

      if (err && typeof err === "object" && "data" in err) {
        const errorData = (err as { data: { error?: string | string[] } }).data;
        if (Array.isArray(errorData.error)) {
          errorMessage = errorData.error.join(" ");
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      }

      setError(errorMessage);
      KaboodleToaster.error(errorMessage);
      logToSentry(`Intake password reset failed`, "error", { uiSessionId });
      console.error(err);
    }
  };

  if (!token) {
    return null; // will redirect via useEffect
  }

  if (isCompleted) {
    return (
      <>
        <IntakeHeader />
        <div className="intake-form-page">
          <div className="intake-card">
            <h2 className="page-title">Password Reset Successful</h2>
            <p className="page-subtitle">
              Your password has been successfully reset. You can now log in with
              your new password.
            </p>

            <div className="prompt-message">
              <Link
                to={`/intake/${organizationSlug || "allan-koba"}/auth/login`}
                className="prompt-link"
              >
                Back to login
              </Link>
            </div>
          </div>
        </div>
        <KaboodleToaster.ToastContainer />
      </>
    );
  }

  return (
    <>
      <IntakeHeader />
      <div className="intake-form-page">
        <div className="intake-card">
          <h2 className="page-title">Reset Your Password</h2>
          <p className="page-subtitle">Enter your new password below.</p>

          <form onSubmit={handleFormSubmit(onSubmit)}>
            <ControlledInput
              rules={{
                required: "Password is required",
                minLength: {
                  value: 8,
                  message: "Password must be at least 8 characters long",
                },
                pattern: {
                  value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                  message:
                    "Password must contain at least one uppercase letter, one lowercase letter, and one number",
                },
              }}
              control={control}
              name="password1"
              label="New Password"
              errors={errors}
              maxLength={128}
              onSave={handleFieldChange}
              componentType={TextInput}
              type="password"
            />

            {/* Password requirements */}
            <div className="password-requirements">
              <p className="password-requirements-title">
                Password must contain:
              </p>
              <ul className="password-requirements-list">
                <li>At least 8 characters</li>
                <li>At least one uppercase letter (A-Z)</li>
                <li>At least one lowercase letter (a-z)</li>
                <li>At least one number (0-9)</li>
              </ul>
            </div>

            <ControlledInput
              rules={{
                required: "Please confirm your password",
                validate: (value: string) =>
                  value === password1 || "Passwords do not match",
              }}
              control={control}
              name="password2"
              label="Confirm New Password"
              errors={errors}
              maxLength={128}
              onSave={handleFieldChange}
              componentType={TextInput}
              type="password"
            />

            {error && <div className="intake-form-page--Error">{error}</div>}

            <Button
              onClick={(e) => {
                e.preventDefault();
                // Check for validation errors first
                if (Object.keys(errors).length > 0) {
                  const errorMessages = Object.values(errors)
                    .map((error) => error?.message)
                    .filter(Boolean);
                  const toastMessage =
                    errorMessages.length > 0
                      ? `Please fix the following: ${errorMessages.join(", ")}`
                      : "Please check your password requirements and try again.";

                  KaboodleToaster.error(toastMessage);
                  return;
                }
                // If no errors, proceed with form submission
                handleFormSubmit(onSubmit)(e);
              }}
              type="submit"
              className="intake-form-page--Button"
              disabled={isSubmitting || isLoading}
            >
              {isSubmitting || isLoading ? "Resetting..." : "Reset Password"}
            </Button>
          </form>

          <div className="prompt-message">
            Remember your password?{" "}
            <Link
              to={`/intake/${organizationSlug || "allan-koba"}/auth/login`}
              className="prompt-link"
            >
              Back to login
            </Link>
          </div>
        </div>
      </div>
      <KaboodleToaster.ToastContainer />
    </>
  );
};

export default ResetPasswordIntake;
